import * as SentryBrowser from '@sentry/browser';
import { Sentry } from '@sonnen/web-utils';
import packageJson from '../../../package.json';

/**
 * Sentry initialization for error tracking and performance monitoring.
 * Follows sonnen-app patterns for consistent error reporting across applications.
 *
 * Pattern mirrored from sonnen-app:
 * - Initialize early before React render
 * - Set release from package.json
 * - Set dist to identify platform (web)
 * - Set environment from VITE_ENV
 */

let sentryInstance: Sentry | null = null;

/**
 * Initialize Sentry with configuration from environment variables.
 * Should be called early in the application lifecycle, before React renders.
 *
 * Mirrors sonnen-app's initSentry() pattern:
 * - DSN from environment
 * - Release: package.json name@version
 * - Dist: platform identifier (web)
 * - Environment: NODE_ENV/VITE_ENV
 *
 * @returns The initialized Sentry instance or null if configuration is missing
 */
export const initSentry = (): Sentry | null => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_ENV || import.meta.env.MODE || 'development';

  if (!dsn) {
    return null;
  }

  try {
    // Create Sentry instance (automatically initializes Sentry SDK)
    sentryInstance = new Sentry(environment, dsn, true);

    // Configure additional Sentry options following sonnen-app pattern
    SentryBrowser.setTag('dist', 'web');
    SentryBrowser.setTag('release', `${packageJson.name}@${packageJson.version}`);

    return sentryInstance;
  } catch {
    return null;
  }
};

/**
 * Get the current Sentry instance.
 * Returns null if Sentry hasn't been initialized.
 */
export const getSentryInstance = (): Sentry | null => {
  return sentryInstance;
};

/**
 * Check if Sentry is initialized and ready to use.
 */
export const isSentryEnabled = (): boolean => {
  return sentryInstance !== null;
};
